/**
 * Thin papaparse wrapper for the CSV viewer.
 *
 * Cadwork timber tally CSVs (3-line metadata + inch/foot dimensions) are parsed with
 * {@link parseCadworkTimberCsvForDisplay} instead of PapaParse so columns stay aligned.
 *
 * Returns a uniform shape with:
 *  - `headers` — column labels
 *  - `rows` — body rows (always padded to header width so the table renders consistently)
 *  - `numericCols` — indices of columns that look numeric (used for right-alignment)
 */

import Papa, { type ParseResult } from 'papaparse';
import { isCadworkTimberCsv, parseCadworkTimberCsvForDisplay } from './proposalCSVParser';

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
  numericCols: Set<number>;
  delimiter: string;
  parseErrors: string[];
}

const NUMERIC_RE = /^-?\(?\$?\s*\d{1,3}(?:[ ,_']\d{3})*(?:\.\d+)?\)?\s*%?$/;

function looksNumeric(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  return NUMERIC_RE.test(v);
}

function dedupeHeaders(raw: string[]): string[] {
  const counts = new Map<string, number>();
  return raw.map((h, i) => {
    const base = (h ?? '').trim() || `Column ${i + 1}`;
    const seen = counts.get(base) ?? 0;
    counts.set(base, seen + 1);
    return seen === 0 ? base : `${base} (${seen + 1})`;
  });
}

function parseGenericCsvText(text: string): ParsedCsv {
  const trimmed = text.replace(/^\uFEFF/, '');
  const result: ParseResult<string[]> = Papa.parse<string[]>(trimmed, {
    skipEmptyLines: 'greedy',
    delimiter: '',
    dynamicTyping: false,
    header: false,
  });

  const data = (result.data || []).filter(r => Array.isArray(r));
  if (data.length === 0) {
    return {
      headers: [],
      rows: [],
      numericCols: new Set(),
      delimiter: result.meta?.delimiter || ',',
      parseErrors: (result.errors || []).slice(0, 5).map(e => e.message),
    };
  }

  const headerRowRaw = data[0] || [];
  const bodyRaw = data.slice(1);
  const colCount = Math.max(headerRowRaw.length, ...bodyRaw.map(r => r.length));

  const headers = dedupeHeaders(
    Array.from({ length: colCount }, (_, i) => (headerRowRaw[i] ?? '').toString()),
  );

  const rows: string[][] = bodyRaw.map(r => {
    const out: string[] = new Array(colCount);
    for (let i = 0; i < colCount; i++) out[i] = (r[i] ?? '').toString();
    return out;
  });

  const numericCols = new Set<number>();
  if (rows.length > 0) {
    for (let c = 0; c < colCount; c++) {
      let total = 0;
      let numeric = 0;
      for (const row of rows) {
        const v = row[c] ?? '';
        if (!v.trim()) continue;
        total++;
        if (looksNumeric(v)) numeric++;
      }
      if (total >= 1 && numeric / total >= 0.7) numericCols.add(c);
    }
  }

  return {
    headers,
    rows,
    numericCols,
    delimiter: result.meta?.delimiter || ',',
    parseErrors: (result.errors || []).slice(0, 5).map(e => e.message),
  };
}

function parseCadworkTimberTallyCsv(text: string, options?: { trueDimensions?: boolean }): ParsedCsv {
  const { rows, columns, delimiterLabel, totalDataLines } = parseCadworkTimberCsvForDisplay(text, options);
  const headers = columns.map(col => col.label);
  const numericCols = new Set<number>();
  columns.forEach((col, i) => {
    if (col.kind === 'number' || col.kind === 'dimension') numericCols.add(i);
  });

  const parseErrors: string[] = [];
  if (totalDataLines > 0 && rows.length === 0) {
    parseErrors.push('No timber rows parsed from CSV.');
  }

  return {
    headers,
    rows,
    numericCols,
    delimiter: delimiterLabel === 'Tab' ? '\t' : ',',
    parseErrors,
  };
}

/**
 * Fetch a CSV from a URL and parse it. Throws on network errors; surfaces parse errors
 * via `parseErrors` so the viewer can show a non-fatal warning while still rendering.
 */
export async function fetchAndParseCsv(
  url: string,
  options?: { trueDimensions?: boolean },
): Promise<ParsedCsv> {
  const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
  if (!res.ok) throw new Error(`Failed to fetch CSV (HTTP ${res.status})`);
  const text = await res.text();
  return parseCsvText(text, options);
}

export function parseCsvText(text: string, options?: { trueDimensions?: boolean }): ParsedCsv {
  const trimmed = text.replace(/^\uFEFF/, '');
  if (isCadworkTimberCsv(trimmed)) {
    return parseCadworkTimberTallyCsv(trimmed, options);
  }
  return parseGenericCsvText(trimmed);
}
